import React from 'react'
import {
  Clock,
  Calendar,
  Award,
  AlertTriangle,
  CheckCircle,
  Play,
  Star,
  ExternalLink,
  Download,
  Users,
  DollarSign,
  Loader2
} from 'lucide-react'
import { Course, TeamCourse } from '@/lib/real-data-service'

interface CourseCardProps {
  course: Course | null
  showTeamData?: boolean
  loading?: boolean
  onStartCourse?: (courseId: string) => void
  onViewDetails?: (courseId: string) => void
  onDownloadCertificate?: (courseId: string) => void
}

export function CourseCard({
  course,
  showTeamData = false,
  loading = false,
  onStartCourse,
  onViewDetails,
  onDownloadCertificate
}: CourseCardProps) {
  // Show loading skeleton if loading or course is null
  if (loading || !course) {
    return (
      <div className="card-hover group">
        {/* Header Loading */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center flex-1 min-w-0">
            <div className="w-12 h-12 bg-brand-gray/20 rounded-monumenta flex items-center justify-center mr-4 flex-shrink-0 animate-pulse">
              <Loader2 className="animate-spin text-brand-gray" size={16} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="h-5 bg-brand-gray/20 rounded animate-pulse mb-2 w-3/4"></div>
              <div className="h-4 bg-brand-gray/20 rounded animate-pulse w-1/2"></div>
            </div>
          </div>
          <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
            <div className="h-6 bg-brand-gray/20 rounded-full animate-pulse w-16"></div>
          </div>
        </div>

        {/* Description Loading */}
        <div className="space-y-2 mb-4">
          <div className="h-4 bg-brand-gray/20 rounded animate-pulse w-full"></div>
          <div className="h-4 bg-brand-gray/20 rounded animate-pulse w-2/3"></div>
        </div>

        {/* Details Loading */}
        <div className="space-y-2 mb-4">
          <div className="h-4 bg-brand-gray/20 rounded animate-pulse w-1/2"></div>
          <div className="h-4 bg-brand-gray/20 rounded animate-pulse w-1/3"></div>
        </div>

        {/* Actions Loading */}
        <div className="flex items-center justify-between pt-4 border-t border-brand-gray/20">
          <div className="h-4 bg-brand-gray/20 rounded animate-pulse w-16"></div>
          <div className="flex space-x-2">
            <div className="h-8 bg-brand-gray/20 rounded animate-pulse w-16"></div>
            <div className="h-8 bg-brand-gray/20 rounded animate-pulse w-16"></div>
          </div>
        </div>
      </div>
    )
  }

  const getStatusColor = (status: Course['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-success/20 text-success'
      case 'in-progress':
        return 'bg-warning/20 text-warning'
      case 'not-started':
        return 'bg-brand-gray/20 text-brand-gray'
      case 'cancelled':
        return 'bg-error/20 text-error'
      default:
        return 'bg-brand-gray/20 text-brand-gray'
    }
  }

  const getStatusText = (status: Course['status']) => {
    const statusMap: Record<Course['status'], string> = {
      'not-started': 'Não Iniciado',
      'in-progress': 'Em Progresso',
      'completed': 'Concluído',
      'cancelled': 'Cancelado',
      'available': 'Disponível',
      'draft': 'Rascunho'
    }
    return statusMap[status] || status
  }

  const getPriorityColor = (priority: Course['priority']) => {
    switch (priority) {
      case 'critical':
        return 'bg-error/20 text-error'
      case 'high':
        return 'bg-warning/20 text-warning'
      case 'medium':
        return 'bg-info/20 text-info'
      case 'low':
        return 'bg-success/20 text-success'
      default:
        return 'bg-brand-gray/20 text-brand-gray'
    }
  }

  const getPriorityText = (priority: Course['priority']) => {
    if (!priority) return 'Sem prioridade'
    const priorityMap: Record<NonNullable<Course['priority']>, string> = {
      critical: 'Crítico',
      high: 'Alto',
      medium: 'Médio',
      low: 'Baixo'
    }
    return priorityMap[priority] || priority
  }

  const getCategoryIcon = (category: Course['category']) => {
    switch (category) {
      case 'technical':
        return ''
      case 'behavioral':
        return ''
      case 'compliance':
        return ''
      case 'leadership':
        return ''
      case 'external':
        return ''
      default:
        return ''
    }
  }

  const isOverdue = course.requiredBy && 
    new Date(course.requiredBy) < new Date() && 
    course.status !== 'completed'

  const teamCourse = showTeamData ? (course as TeamCourse) : null

  return (
    <div className="card-hover group">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center flex-1 min-w-0">
          <div className="w-12 h-12 bg-brand-primary rounded-monumenta flex items-center justify-center mr-4 flex-shrink-0">
            <span className="text-2xl">{getCategoryIcon(course.category)}</span>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-lg font-semibold text-white mb-1 group-hover:text-brand-primary transition-colors truncate">
              {course.title}
            </h3>
            <p className="text-brand-gray text-sm flex items-center">
              {course.provider}
              {course.type === 'external' && (
                <ExternalLink size={12} className="ml-1 opacity-60" />
              )}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
          <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(course.status)}`}>
            {getStatusText(course.status)}
          </span>
          {course.priority === 'critical' || course.priority === 'high' ? (
            <span className={`px-2 py-1 rounded-full text-xs ${getPriorityColor(course.priority)}`}>
              {getPriorityText(course.priority)}
            </span>
          ) : null}
        </div>
      </div>

      {/* Overdue Alert */}
      {isOverdue && (
        <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-monumenta">
          <div className="flex items-center text-error">
            <AlertTriangle size={16} className="mr-2" />
            <span className="text-sm font-medium">
              Vencimento em {new Date(course.requiredBy!).toLocaleDateString('pt-BR')}
            </span>
          </div>
        </div>
      )}

      {/* Description */}
      <p className="text-gray-300 text-sm mb-4 line-clamp-2">
        {course.description}
      </p>

      {/* Progress Bar for In-Progress Courses */}
      {course.status === 'in-progress' && (
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-white">Progresso</span>
            <span className="text-sm text-brand-primary font-medium">{course.progress}%</span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${course.progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Team Completion Data */}
      {teamCourse && (
        <div className="mb-4 p-3 bg-brand-dark-light rounded-monumenta">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-white">Conclusão da Equipe</span>
            <span className="text-sm text-brand-primary font-medium">{teamCourse.teamCompletionRate}%</span>
          </div>
          <div className="progress-bar mb-2">
            <div 
              className="progress-fill" 
              style={{ width: `${teamCourse.teamCompletionRate}%` }}
            />
          </div>
          <div className="flex items-center text-xs text-brand-gray">
            <Users size={12} className="mr-1" />
            <span>{teamCourse.enrolledEmployees.length} colaboradores inscritos</span>
          </div>
        </div>
      )}

      {/* Course Details */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center text-sm text-gray-300">
          <Clock className="text-brand-primary mr-2" size={14} />
          <span>{course.duration}h de duração</span>
        </div>
        
        {course.startDate && (
          <div className="flex items-center text-sm text-gray-300">
            <Calendar className="text-info mr-2" size={14} />
            <span>
              Iniciado em {new Date(course.startDate).toLocaleDateString('pt-BR')}
            </span>
          </div>
        )}

        {course.completionDate && (
          <div className="flex items-center text-sm text-gray-300">
            <CheckCircle className="text-success mr-2" size={14} />
            <span>
              Concluído em {new Date(course.completionDate).toLocaleDateString('pt-BR')}
            </span>
          </div>
        )}
        
        {course.cost && (
          <div className="flex items-center text-sm text-gray-300">
            <DollarSign className="text-warning mr-2" size={14} />
            <span>
              {course.currency === 'USD' ? 'US$' : 'R$'} {course.cost.toLocaleString()}
            </span>
          </div>
        )}

        {course.rating && (
          <div className="flex items-center text-sm text-gray-300">
            <Star className="text-warning mr-2" size={14} />
            <span>{course.rating}/5 estrelas</span>
          </div>
        )}
      </div>

      {/* Skills Tags */}
      {course.skills && course.skills.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-1">
            {course.skills.slice(0, 3).map((skill, index) => (
              <span 
                key={index}
                className="px-2 py-1 bg-brand-dark-light text-brand-gray text-xs rounded-monumenta"
              >
                {skill}
              </span>
            ))}
            {course.skills.length > 3 && (
              <span className="px-2 py-1 bg-brand-dark-light text-brand-gray text-xs rounded-monumenta">
                +{course.skills.length - 3} mais
              </span>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-brand-gray/20">
        <div className="text-xs text-brand-gray capitalize">
          {course.category === 'technical' && 'Técnico'}
          {course.category === 'behavioral' && 'Comportamental'}
          {course.category === 'compliance' && 'Compliance'}
          {course.category === 'leadership' && 'Liderança'}
          {course.category === 'external' && 'Externo'}
        </div>
        
        <div className="flex space-x-2">
          {course.status === 'completed' && course.certificateUrl && (
            <button 
              onClick={() => onDownloadCertificate?.(course.id)}
              className="btn-icon btn-secondary group-hover:scale-105 transition-transform"
              title="Baixar Certificado"
            >
              <Download size={14} />
            </button>
          )}
          
          {course.status === 'not-started' && (
            <button 
              onClick={() => onStartCourse?.(course.id)}
              className="btn-primary text-xs py-2 px-4 flex items-center gap-1"
            >
              <Play size={12} />
              Iniciar
            </button>
          )}
          
          {course.status === 'in-progress' && (
            <button 
              onClick={() => onStartCourse?.(course.id)}
              className="btn-primary text-xs py-2 px-4"
            >
              Continuar
            </button>
          )}
          
          <button 
            onClick={() => onViewDetails?.(course.id)}
            className="btn-secondary text-xs py-2 px-4"
          >
            Detalhes
          </button>
        </div>
      </div>

      {/* Feedback for Completed Courses */}
      {course.status === 'completed' && course.feedback && (
        <div className="mt-4 p-3 bg-success/10 border border-success/20 rounded-monumenta">
          <p className="text-xs text-success italic">
            "{course.feedback}"
          </p>
        </div>
      )}
    </div>
  )
}
